/**
 * The dictation's waveform (board notes, „Waveform"): one bar per 80 ms of voice, newest at the ✓,
 * the history scrolling left under a mask. Drawn on a canvas in `useAnimationFrame`, so no frame is
 * React state; the level is read from the recorder when the frame wants it.
 *
 *   · flat      waiting for the browser's permission: a level line of dots, breathing
 *   · live      recording: an envelope follower (fast attack, slow release) over the level, a bar
 *               for the loudest moment of each window, the newest one growing in
 *   · collapse  transcribing: the bars fall to dots, newest first, and a highlight runs across them
 *
 * Reduced motion draws a still shape: no scroll, no growth, no shimmer. The ink is `--text`, read
 * off the computed style so a theme change reaches the canvas; never the accent, never a glow.
 */

import { useAnimationFrame } from 'motion/react';
import { useEffect, useRef, type RefObject } from 'react';

import type { WaveMode } from '../model/dictation';
import styles from './Composer.module.css';

const WINDOW_MS = 80;
const BAR = 3;
const PITCH = 6;
const LOW = 3;
const HIGH = 28;
const GROW_MS = 120;
const FALL_MS = 260;
const FALL_STAGGER_MS = 4;
const FALL_STAGGER_CAP_MS = 300;
const SHINE_AFTER_MS = 420;
const SHINE_EVERY_MS = 1300;
const SHINE_SIGMA = 56;
const ATTACK = 0.5;
const RELEASE = 0.12;
const DOT_BELOW = 0.05;
const INK_EVERY_MS = 500;

interface Bar {
  readonly level: number;
  readonly born: number;
}

const easeOut = (t: number) => 1 - (1 - t) ** 3;

/** A fixed, speech-like shape for reduced motion: the same every time, moving never. */
const stillLevel = (index: number) => {
  const phrase = 0.5 + 0.5 * Math.sin(index * 0.072) * Math.sin(index * 0.03 + 1.3);
  const syllable = Math.abs(Math.sin(index * 1.17)) ** 1.4;
  return phrase > 0.22 ? Math.min(1, (phrase - 0.22) * 3.2) * (0.28 + 0.62 * syllable) : 0;
};

function pill(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number) {
  const radius = Math.min(width, height) / 2;
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fill();
}

export interface DictationWaveProps {
  readonly mode: WaveMode;
  readonly readLevel: () => number;
  readonly reducedMotion: boolean;
  /** The dot by the timer, which breathes with the voice. */
  readonly dot: RefObject<HTMLSpanElement | null>;
}

export function DictationWave({ mode, readLevel, reducedMotion, dot }: DictationWaveProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const bars = useRef<Bar[]>([]);
  const envelope = useRef({ level: 0, peak: 0, lastPush: 0, modeAt: 0, ink: '', inkAt: -Infinity });
  const size = useRef({ width: 0, height: 0 });

  // A new dictation starts from an empty history; transcribing keeps the one it falls from.
  useEffect(() => {
    const now = performance.now();
    envelope.current.modeAt = now;
    if (mode !== 'collapse') {
      bars.current = [];
      envelope.current.lastPush = now;
      envelope.current.level = 0;
    }
  }, [mode]);

  useEffect(() => {
    const node = canvas.current;
    if (node === null) return;
    const fit = () => {
      const box = node.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      size.current = { width: box.width, height: box.height };
      node.width = Math.max(1, Math.round(box.width * ratio));
      node.height = Math.max(1, Math.round(box.height * ratio));
      node.getContext('2d')?.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Motion hands the callback the time since the hook mounted; every stamp here is
  // `performance.now()`, so the frame reads that clock too.
  useAnimationFrame(() => {
    const now = performance.now();
    const node = canvas.current;
    const context = node?.getContext('2d');
    if (node === null || context === null || context === undefined) return;
    const state = envelope.current;
    const { width, height } = size.current;
    if (now - state.inkAt > INK_EVERY_MS) {
      state.ink = getComputedStyle(node).getPropertyValue('--text').trim();
      state.inkAt = now;
    }
    context.clearRect(0, 0, width, height);
    context.fillStyle = state.ink;
    const middle = height / 2;
    const room = Math.ceil(width / PITCH) + 2;

    if (mode === 'flat') {
      context.globalAlpha = reducedMotion ? 0.32 : 0.26 + 0.12 * (0.5 + 0.5 * Math.sin(now / 520));
      for (let x = width - BAR; x > -BAR; x -= PITCH) pill(context, x, middle - LOW / 2, BAR, LOW);
      context.globalAlpha = 1;
      return;
    }

    if (reducedMotion) {
      for (let k = 0, x = width - BAR; x > -BAR; k += 1, x -= PITCH) {
        const level = stillLevel(k);
        const tall = mode === 'collapse' ? LOW : LOW + level * (HIGH - LOW);
        context.globalAlpha = mode === 'collapse' ? 0.45 : level < DOT_BELOW ? 0.42 : 1;
        pill(context, x, middle - tall / 2, BAR, tall);
      }
      context.globalAlpha = 1;
      return;
    }

    if (mode === 'live') {
      const raw = readLevel();
      state.level += (raw - state.level) * (raw > state.level ? ATTACK : RELEASE);
      state.peak = Math.max(state.peak, state.level);
      if (now - state.lastPush >= WINDOW_MS) {
        bars.current.push({ level: state.peak, born: now });
        if (bars.current.length > room) bars.current.splice(0, bars.current.length - room);
        state.peak = 0;
        state.lastPush = now;
      }
      if (dot.current !== null) dot.current.style.transform = `scale(${(0.72 + 0.4 * Math.min(1, state.level * 1.2)).toFixed(3)})`;
    }

    const history = bars.current;
    const drift = mode === 'live' ? Math.min(1, (now - state.lastPush) / WINDOW_MS) : 0;
    const since = now - state.modeAt;
    for (let k = 0; k < history.length; k += 1) {
      const bar = history[history.length - 1 - k];
      const x = width - BAR - (k + drift) * PITCH;
      if (bar === undefined || x < -BAR) break;
      let tall = LOW + (LOW + bar.level * (HIGH - LOW) - LOW) * easeOut(Math.min(1, (now - bar.born) / GROW_MS));
      let alpha = bar.level < DOT_BELOW ? 0.42 : 1;
      if (mode === 'collapse') {
        const fall = easeOut(Math.max(0, Math.min(1, (since - Math.min(k * FALL_STAGGER_MS, FALL_STAGGER_CAP_MS)) / FALL_MS)));
        tall += (LOW - tall) * fall;
        const shining = since - SHINE_AFTER_MS;
        if (shining > 0) {
          const head = ((shining % SHINE_EVERY_MS) / SHINE_EVERY_MS) * (width + 240) - 120;
          alpha = 0.3 + 0.7 * Math.exp(-(((x - head) / SHINE_SIGMA) ** 2));
        } else {
          alpha = 1 - 0.5 * fall;
        }
      }
      context.globalAlpha = alpha;
      pill(context, x, middle - tall / 2, BAR, tall);
    }
    // The lane is never blank: until the history fills it, a quiet baseline of dots waits on the left.
    context.globalAlpha = mode === 'live' ? 0.26 : 0.3;
    for (let x = width - BAR - (history.length + drift) * PITCH; x > -BAR; x -= PITCH) pill(context, x, middle - LOW / 2, BAR, LOW);
    context.globalAlpha = 1;
  });

  return (
    <span className={styles.waveBox}>
      <canvas ref={canvas} className={styles.wave} aria-hidden="true" />
    </span>
  );
}
